'use client'

import { useSearchParams } from 'next/navigation'
import { Block } from '@babel/types'

import { clsx } from 'clsx'

import { FC, useEffect } from 'react'

import { HomeQuery, Setting } from '@/app/graphql/generated'
import { AboutComponent } from '@/app/shared/components/about'
import { IAboutQuery } from '@/app/shared/components/about/about.component'
import { AnimationShowBlockComponent } from '@/app/shared/components/animation/show-block'
import { BarrierComponent } from '@/app/shared/components/barrier'
import { IBarrierQuery } from '@/app/shared/components/barrier/barrier.component'
import { CoursesComponent } from '@/app/shared/components/courses'
import { ExpressCoursesComponent } from '@/app/shared/components/express-courses'
import { GuaranteesComponent } from '@/app/shared/components/guarantees'
import { IGuaranteesQuery } from '@/app/shared/components/guarantees/guarantees.component'
import { PositionComponent } from '@/app/shared/components/position'
import { IPositionQuery } from '@/app/shared/components/position/position.component'
import { ProgramComponent } from '@/app/shared/components/program'
import { ReviewsComponent } from '@/app/shared/components/reviews'
import { SignUpLessonComponent } from '@/app/shared/components/sign-up-lesson'
import { ISignUpLessonQuery } from '@/app/shared/components/sign-up-lesson/sign-up-lesson.component'
import { StartStudyComponent } from '@/app/shared/components/start-study'
import { IStartStudyQuery } from '@/app/shared/components/start-study/start-study.component'
import { TeachingMethodologyComponent } from '@/app/shared/components/teaching-methodology'
import { ITeachingMethodologyQuery } from '@/app/shared/components/teaching-methodology/teaching-methodology.component'
import { WelcomeComponent } from '@/app/shared/components/welcome'
import { IWelcome } from '@/app/shared/components/welcome/welcome.component'
import { getBlock, getDataBySlug, isBlockExist } from '@/app/shared/services/data-formatter'
import useLayout, { ITypeSite } from '@/app/shared/store/layout.store'
import { useMarkersStore } from '@/app/shared/store/markers.store'

//interface
interface IConstructorProps {
  data: HomeQuery
  typeSite: ITypeSite
}

//component
const ConstructorComponent: FC<Readonly<IConstructorProps>> = ({ data, typeSite }) => {
  const searchParams = useSearchParams()

  const setSocials = useLayout((state) => state.setSocials)

  const handleChangeGlobalStore = useMarkersStore((state) => state.handleChangeGlobalStore)

  useEffect(() => {
    if (data?.page?.settings) setSocials(data?.page?.settings as Setting[])
  }, [data, setSocials])

  useEffect(() => {
    if (searchParams.toString()) {
      handleChangeGlobalStore({ markers: searchParams.toString() })
    }
  }, [searchParams])

  //return 1
  return (
    <main
      className={clsx(
        'table:[&>*:not(:last-child):not(:first-child)]:mb-[140px] mt-[109px] flex-grow gap-40 before:absolute before:-top-[250px] before:left-0 before:h-[400px] before:w-full laptop:mt-[137px] [&>*:not(:last-child):not(:first-child)]:mb-[82px] desktop:[&>*:not(:last-child):not(:first-child)]:mb-40',
        {
          'before:bg-content2': typeSite === 'offline' || typeSite === 'kids',
          'before:bg-secondary': typeSite === 'online',
          'before:bg-success': typeSite === 'online-espanol' || typeSite === 'online-deutsch',
        },
      )}
    >
      {data.page?.blocks?.map((el) => (
        <div key={`${el?.type}_${el?.id}`}>
          {el?.type === 'banner' && isBlockExist('banner', data.page) && (
            <WelcomeComponent
              id={el.type}
              data={getDataBySlug('banner', data.page) as IWelcome}
              typeSite={typeSite}
            />
          )}
          {el?.type === 'language-barrier-blog' &&
            isBlockExist('language-barrier-blog', data.page) && (
              <BarrierComponent
                id={el.type}
                data={getDataBySlug('language-barrier-blog', data.page) as IBarrierQuery}
                typeSite={typeSite}
              />
            )}
          {el?.type === 'teaching-methods-block' &&
            isBlockExist('teaching-methods-block', data.page) && (
              <TeachingMethodologyComponent
                id={el.type}
                data={
                  getDataBySlug('teaching-methods-block', data.page) as ITeachingMethodologyQuery
                }
              />
            )}
          {el?.type === 'about-school' && isBlockExist('about-school', data.page) && (
            <AboutComponent
              id={el.type}
              data={getDataBySlug('about-school', data.page) as IAboutQuery}
            />
          )}
          {el?.type === 'language-levels' && isBlockExist('language-levels', data.page) && (
            <ProgramComponent
              id={el.type}
              typeSite={typeSite}
              data={getBlock('language-levels', data.page) as Block}
            />
          )}
          {el?.type === 'guarantees-block' && isBlockExist('guarantees-block', data.page) && (
            <GuaranteesComponent
              id={el.type}
              data={getDataBySlug('guarantees-block', data.page) as IGuaranteesQuery}
            />
          )}
          {el?.type === 'training-courses' && isBlockExist('training-courses', data.page) && (
            <AnimationShowBlockComponent
              initial={{
                opacity: 0,
                y: 30,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
                amount: 0,
              }}
            >
              <CoursesComponent id={el.type} data={getBlock('training-courses', data.page)} />
            </AnimationShowBlockComponent>
          )}
          {el?.type === 'express-courses-cards' &&
            isBlockExist('express-courses-cards', data.page) && (
              <ExpressCoursesComponent
                id={el.type}
                data={getDataBySlug('express-courses-cards', data.page, true) as Block}
              />
            )}
          {el?.type === 'comments' && isBlockExist('comments', data.page) && (
            <ReviewsComponent id={el.type} data={getBlock('comments', data.page) as Block} />
          )}
          {el?.type === 'steps-learning' && isBlockExist('steps-learning', data.page) && (
            <StartStudyComponent
              id={el.type}
              typeSite={typeSite}
              data={getDataBySlug('steps-learning', data.page) as IStartStudyQuery}
            />
          )}
          {el?.type === 'feedback-form' && isBlockExist('feedback-form', data.page) && (
            <SignUpLessonComponent
              id={el.type}
              data={getDataBySlug('feedback-form', data.page) as ISignUpLessonQuery}
              typeSite={typeSite}
            />
          )}
          {el?.type === 'metro-block' && isBlockExist('metro-block', data.page) && (
            <PositionComponent
              id={el.type}
              data={getDataBySlug('metro-block', data.page) as IPositionQuery}
            />
          )}
        </div>
      ))}
    </main>
  )
}

export default ConstructorComponent
